import React, { useEffect, useRef, useState } from 'react';
import { registerSubmitterPrompt, SHARED_ACCOUNT_EMAIL } from '../utils/sharedAccount';

const EMPTY = { name: '', phone: '', email: '', organisation: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (values) => {
  const errors = {};
  if (!values.name.trim()) errors.name = 'Please enter your full name.';
  const digits = values.phone.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) errors.phone = 'Please enter a valid phone number.';
  const email = values.email.trim().toLowerCase();
  if (!EMAIL_RE.test(email)) {
    errors.email = 'Please enter a valid email address.';
  } else if (email === SHARED_ACCOUNT_EMAIL) {
    errors.email = 'Please enter your own email - the confirmation is sent there.';
  }
  if (!values.organisation.trim()) errors.organisation = 'Please enter your organisation.';
  return errors;
};

/**
 * Asks shared-login users who they are before a questionnaire is submitted.
 * Mounted once in App; opened through collectSubmitterDetails() in utils/sharedAccount.
 */
export default function SubmitterDetailsModal() {
  const [request, setRequest] = useState(null); // { resolve }
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const firstFieldRef = useRef(null);

  useEffect(() => registerSubmitterPrompt((initial) => new Promise((resolve) => {
    setValues({ ...EMPTY, ...(initial || {}) });
    setErrors({});
    setRequest({ resolve });
  })), []);

  const close = (result) => {
    if (request) request.resolve(result);
    setRequest(null);
  };

  useEffect(() => {
    if (!request) return undefined;
    firstFieldRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        request.resolve(null);
        setRequest(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request]);

  if (!request) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    close({
      name: values.name.trim(),
      phone: values.phone.trim(),
      email: values.email.trim().toLowerCase(),
      organisation: values.organisation.trim()
    });
  };

  const fields = [
    { name: 'name', label: 'Full name', type: 'text', autoComplete: 'name', placeholder: 'e.g. Priya Sharma' },
    { name: 'phone', label: 'Phone number', type: 'tel', autoComplete: 'tel', placeholder: 'e.g. +91 98765 43210' },
    { name: 'email', label: 'Email address', type: 'email', autoComplete: 'email', placeholder: 'you@example.com' },
    { name: 'organisation', label: 'Organisation', type: 'text', autoComplete: 'organization', placeholder: 'Company / institute' }
  ];

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="submitter-details-title"
    >
      <form
        onSubmit={handleSubmit}
        noValidate
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto"
      >
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-5">
          <h2 id="submitter-details-title" className="text-xl font-bold text-white">Your details</h2>
          <p className="text-blue-100 text-sm mt-1">
            All fields are required. A confirmation of your submission will be sent to the email you enter.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {fields.map((field, index) => (
            <div key={field.name}>
              <label htmlFor={`submitter-${field.name}`} className="block text-sm font-semibold text-gray-700 mb-1">
                {field.label} <span className="text-red-600" aria-hidden="true">*</span>
              </label>
              <input
                ref={index === 0 ? firstFieldRef : undefined}
                id={`submitter-${field.name}`}
                name={field.name}
                type={field.type}
                autoComplete={field.autoComplete}
                placeholder={field.placeholder}
                value={values[field.name]}
                onChange={handleChange}
                required
                aria-invalid={Boolean(errors[field.name])}
                aria-describedby={errors[field.name] ? `submitter-${field.name}-error` : undefined}
                className={`w-full px-4 py-3 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors[field.name] ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors[field.name] && (
                <p id={`submitter-${field.name}-error`} className="text-xs text-red-600 mt-1">{errors[field.name]}</p>
              )}
            </div>
          ))}
        </div>

        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => close(null)}
            className="flex-1 min-h-[44px] px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 min-h-[44px] px-4 py-2.5 rounded-lg bg-blue-900 text-white font-semibold hover:bg-blue-800"
          >
            Submit questionnaire
          </button>
        </div>
      </form>
    </div>
  );
}
